import { fundingAllowanceExceeded, localizeFundingAllowance, } from '../amount/funding-bounds.js';
import { fundingRateIsCurrent, fundingSettlementValue, } from '../amount/fx.js';
import { addUnits, decimalToUnits } from '../amount/money.js';
import { providerAllowanceUnits } from '../amount/provider-allowance.js';
import { providerFundingMethod } from '../bank-transfers/types.js';
import { FundingCurrencyError, } from '../operations/currency-diagnostics.js';
import { cardPaymentMethod } from './card-payment.js';
export function fundingQuoteAmounts(review) {
    return {
        ...(review.estimate.targetUnits !== undefined
            ? { localTargetUnits: review.estimate.targetUnits }
            : {}),
        principalUnits: review.estimate.principalUnits,
        feeUnits: review.estimate.feeUnits,
        totalUnits: review.estimate.totalUnits,
        receivedUnits: review.receivedUnits,
        estimatedOutput: review.estimatedOutput,
    };
}
const MAX_UNITS = 99999999999999999999n;
/** Client presentation validation, never backend proof of a quote or payment authorization. */
export function reviewQuote(quote, intent, now, purpose = 'execution') {
    if (purpose === 'execution')
        cardPaymentMethod(intent.method, intent.providerPaymentMethodId);
    else if (intent.providerPaymentMethodId || (intent.method === 'card' && quote.paymentMethod))
        throw new Error('onramp_card_binding_invalid');
    if (quote.source.asset.code !== intent.fiatCurrency ||
        (intent.settlement.caip19
            ? quote.destination.asset.caip19 !== intent.settlement.caip19
            : quote.destination.asset.code !== intent.settlement.provider_asset_code)) {
        const source = quote.source.asset.code !== intent.fiatCurrency;
        throw new FundingCurrencyError('onramp_quote_intent_mismatch', {
            event: 'onramp.quote_currency_mismatch',
            currency: intent.fiatCurrency,
            reason: source ? 'source' : 'destination',
            providerCurrency: source ? quote.source.asset.code : quote.destination.asset.code,
        });
    }
    if (!/^[A-Z]{3}$/.test(intent.fiatCurrency) ||
        !/^[1-9]\d{0,19}$/.test(intent.principalUnits) ||
        !Number.isInteger(intent.fiatDecimals) ||
        intent.fiatDecimals < 0 ||
        intent.fiatDecimals > 4 ||
        ((purpose === 'execution' || quote.wallet) && quote.wallet?.address !== intent.destination) ||
        quote.wallet?.tag !== undefined ||
        quote.feeBehavior !== (intent.receiveTarget ? 'inclusive' : 'exclusive') ||
        !intent.settlement.provider_asset_code ||
        !Number.isInteger(intent.settlement.decimals) ||
        intent.settlement.decimals < 0 ||
        intent.settlement.decimals > 18 ||
        ((purpose === 'execution' || intent.method !== 'card') &&
            quote.paymentMethod?.type !== providerFundingMethod(intent.method)) ||
        (purpose === 'execution' &&
            intent.method === 'card' &&
            quote.paymentMethod?.id !== intent.providerPaymentMethodId) ||
        !Number.isFinite(Date.parse(quote.expiresAt)) ||
        !Number.isFinite(now))
        throw new Error('onramp_quote_intent_mismatch');
    if (intent.settlement.caip19 &&
        (intent.receiveTarget ||
            !Number.isInteger(quote.slippageBps) ||
            quote.slippageBps === undefined ||
            quote.slippageBps < 0 ||
            quote.slippageBps > 10000))
        throw new FundingCurrencyError('onramp_token_quote_invalid', {
            event: 'onramp.token_quote_rejected',
            currency: intent.fiatCurrency,
            reason: intent.receiveTarget ? 'unsupported_receive_target' : 'invalid_slippage',
        });
    if (intent.settlement.caip19 &&
        (!quote.paymentDisclosures.some((disclosure) => disclosure.id === 'gateway-token' && disclosure.version === '1') ||
            quote.paymentDisclosures.some((disclosure) => disclosure.id !== 'gateway-token' || disclosure.version !== '1')))
        throw new FundingCurrencyError('onramp_purchase_disclosure_unavailable', {
            event: 'onramp.token_quote_rejected',
            currency: intent.fiatCurrency,
            reason: 'unsupported_disclosure',
        });
    const { partner, ecosystem } = quote.fees;
    if (partner &&
        ecosystem &&
        (partner.asset.code !== ecosystem.asset.code ||
            decimalToUnits(partner.amount, intent.fiatDecimals, MAX_UNITS) !==
                decimalToUnits(ecosystem.amount, intent.fiatDecimals, MAX_UNITS)))
        throw new Error('onramp_quote_fee_alias_mismatch');
    const fees = [
        quote.fees.network,
        quote.fees.moonpay,
        ecosystem ?? partner,
        quote.fees.defi,
    ].filter((fee) => fee !== undefined);
    const foreignFee = fees.find((fee) => fee.asset.code !== intent.fiatCurrency);
    if (foreignFee)
        throw new FundingCurrencyError('onramp_quote_fee_currency_mismatch', {
            event: 'onramp.quote_currency_mismatch',
            currency: intent.fiatCurrency,
            reason: 'fee',
            providerCurrency: foreignFee.asset.code,
        });
    const feeUnits = addUnits(fees.map((fee) => decimalToUnits(fee.amount, intent.fiatDecimals, MAX_UNITS)), MAX_UNITS);
    const sourceUnits = decimalToUnits(quote.source.amount, intent.fiatDecimals, MAX_UNITS);
    const receivedUnits = decimalToUnits(quote.destination.amount, intent.settlement.decimals, MAX_UNITS);
    const target = intent.receiveTarget;
    if (target) {
        if (!fundingRateIsCurrent(target.fx, now))
            throw new Error('funding_fx_unavailable');
        if (intent.settlement.provider_asset_code !== 'USDC_SOL' || intent.settlement.decimals !== 6)
            throw new Error('onramp_quote_target_asset_unsupported');
        const receivedLocalUnits = fundingSettlementValue(receivedUnits.toString(), intent.fiatCurrency, intent.fiatDecimals, target.fx);
        // Bind the displayed local minor units, retaining MoonPay's actual rounded D6 output.
        if (receivedLocalUnits !== target.localUnits)
            throw new FundingCurrencyError('onramp_quote_target_mismatch', {
                event: 'onramp.quote_target_mismatch',
                currency: intent.fiatCurrency,
                reason: 'output_mismatch',
            });
        if (sourceUnits <= feeUnits)
            throw new Error('onramp_quote_invalid_total');
    }
    const principalUnits = target ? sourceUnits - feeUnits : BigInt(intent.principalUnits);
    const totalUnits = target ? sourceUnits : addUnits([principalUnits, feeUnits], MAX_UNITS);
    // Platform's live response includes fees; its documented exclusive shape echoes principal.
    // Accept only these exact amounts, never an arbitrary provider change or twice-added fees.
    if (!target && sourceUnits !== principalUnits && sourceUnits !== totalUnits)
        throw new Error('onramp_quote_principal_changed');
    // MoonPay wallet quotes can omit numeric allowances by returning 0/0.
    // Keep actual exhausted allowances (positive limit, zero remaining) authoritative.
    const wallet = intent.method === 'apple_pay' || intent.method === 'google_pay';
    const suppliedLimits = Object.entries(quote.limits ?? {}).filter(([, value]) => value);
    const meaningfulLimits = suppliedLimits.filter(([, value]) => !(wallet &&
        value &&
        value.limit.asset.code === value.remaining.asset.code &&
        /^0(?:\.0+)?$/.test(value.limit.amount) &&
        /^0(?:\.0+)?$/.test(value.remaining.amount)));
    const allowanceQuote = { ...quote, limits: Object.fromEntries(meaningfulLimits) };
    const limitsUnavailable = suppliedLimits.length > 0 && meaningfulLimits.length === 0;
    const limits = compatibleLimits(allowanceQuote, intent);
    const localizedAllowances = target
        ? Object.entries(allowanceQuote.limits ?? {}).flatMap(([period, value]) => {
            if (!value || (period !== 'daily' && period !== 'monthly' && period !== 'yearly'))
                return [];
            const localized = localizeFundingAllowance(period, value, intent.fiatCurrency, intent.fiatDecimals, target.fx);
            return localized ? [localized] : [];
        })
        : [];
    const unknownAllowance = Boolean(target &&
        Object.values(allowanceQuote.limits ?? {}).filter(Boolean).length !==
            localizedAllowances.length);
    const displayedLimits = target ? localizedAllowances : limits;
    // A supplied exhausted allowance blocks even when denominated differently.
    const limitExceeded = Object.values(allowanceQuote.limits ?? {}).some((limit) => /^0(?:\.0+)?$/.test(limit?.remaining.amount ?? '')) ||
        (target
            ? localizedAllowances.some((limit) => fundingAllowanceExceeded(limit, totalUnits.toString(), intent.fiatCurrency, intent.fiatDecimals, target.fx))
            : limits.some((limit) => BigInt(limit.remainingUnits) < principalUnits));
    const state = Date.parse(quote.expiresAt) <= now
        ? 'expired'
        : purpose === 'execution' && quote.challenge
            ? 'challenge_required'
            : limitExceeded
                ? 'limit_exceeded'
                : purpose === 'indicative'
                    ? 'indicative'
                    : unknownAllowance
                        ? 'not_executable'
                        : quote.executable && quote.signature.length > 0
                            ? 'ready'
                            : 'not_executable';
    const unavailableLimit = unknownAllowance
        ? Object.values(allowanceQuote.limits ?? {}).find((limit) => limit &&
            (!target?.fx.ratesD9[limit.remaining.asset.code] ||
                !target.fx.ratesD9[limit.limit.asset.code]))
        : undefined;
    return {
        method: intent.method,
        ...(intent.providerPaymentMethodId
            ? { providerPaymentMethodId: intent.providerPaymentMethodId }
            : {}),
        ...(unknownAllowance
            ? {
                diagnostic: {
                    event: 'onramp.limit_conversion_unavailable',
                    currency: intent.fiatCurrency,
                    reason: unavailableLimit ? 'missing_rate' : 'invalid_limit',
                    ...(unavailableLimit
                        ? { providerCurrency: unavailableLimit.remaining.asset.code }
                        : {}),
                },
            }
            : limitsUnavailable
                ? {
                    diagnostic: {
                        event: 'onramp.limits_not_supplied',
                        currency: intent.fiatCurrency,
                        reason: 'zero_pair',
                    },
                }
                : {}),
        state,
        estimate: {
            estimatedOutput: quote.exchangeRateType === 'floating',
            currency: intent.fiatCurrency,
            decimals: intent.fiatDecimals,
            principalUnits: principalUnits.toString(),
            ...(target
                ? {
                    targetUnits: target.localUnits,
                    receivedLocalUnits: fundingSettlementValue(receivedUnits.toString(), intent.fiatCurrency, intent.fiatDecimals, target.fx),
                }
                : {}),
            feeUnits: feeUnits.toString(),
            totalUnits: totalUnits.toString(),
        },
        receivedUnits: decimalToUnits(quote.destination.amount, intent.settlement.decimals, MAX_UNITS).toString(),
        estimatedOutput: quote.exchangeRateType === 'floating',
        expiresAt: quote.expiresAt,
        limits: displayedLimits,
        ...(limitsUnavailable ? { limitsUnavailable: true } : {}),
        disclosures: quote.paymentDisclosures,
        ...(intent.settlement.caip19 ? { slippageBps: quote.slippageBps } : {}),
        ...(quote.challenge ? { challenge: quote.challenge } : {}),
    };
}
function compatibleLimits(quote, intent) {
    const result = [];
    for (const period of ['daily', 'monthly', 'yearly']) {
        const entry = quote.limits?.[period];
        if (!entry ||
            entry.limit.asset.code !== intent.fiatCurrency ||
            entry.remaining.asset.code !== intent.fiatCurrency)
            continue;
        const { limit, remaining } = providerAllowanceUnits(entry.limit.amount, entry.remaining.amount, intent.fiatDecimals, MAX_UNITS);
        result.push({ period, limitUnits: limit.toString(), remainingUnits: remaining.toString() });
    }
    return result;
}
